(() => {
  const ROOT_CLASS = 'moretti-inbox-hidden';
  const PURCHASE_CLASS = 'moretti-inbox-purchase-active';
  const PURCHASE_SELECTOR = '[id^="MainProduct-"] variant-selects, [id^="MainProduct-"] product-form';
  const mobile = window.matchMedia('(max-width: 749px)');
  const purchaseVisibility = new Map();
  let purchaseObserver;

  const observePurchaseControls = () => {
    if (!purchaseObserver) return;
    purchaseVisibility.forEach((visible, element) => {
      if (!element.isConnected) {
        purchaseObserver.unobserve(element);
        purchaseVisibility.delete(element);
      }
    });
    document.querySelectorAll(PURCHASE_SELECTOR).forEach((element) => {
      if (!purchaseVisibility.has(element)) {
        purchaseVisibility.set(element, false);
        purchaseObserver.observe(element);
      }
    });
  };
  const OVERLAY_ROOT_SELECTOR =
    'cart-drawer, cart-notification, quick-add-modal, product-modal, details-modal, [data-moretti-precheckout], dialog, [role="dialog"][aria-modal="true"]';
  const VISIBLE_MODAL_SELECTORS = [
    'cart-notification.active',
    '[data-moretti-precheckout]:not([hidden]):not([aria-hidden="true"])',
    'quick-add-modal[open]',
    'product-modal[open]',
    'dialog[open]',
    '[role="dialog"][aria-modal="true"]',
  ];

  const isVisible = (element) => {
    if (!element || element.hidden || element.getAttribute('aria-hidden') === 'true') return false;

    if (typeof element.checkVisibility === 'function') {
      return element.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true });
    }

    let current = element;
    while (current) {
      if (current.hidden || current.getAttribute?.('aria-hidden') === 'true') return false;

      const style = window.getComputedStyle(current);
      if (
        style.display === 'none' ||
        style.visibility === 'hidden' ||
        style.visibility === 'collapse' ||
        Number(style.opacity) === 0
      ) {
        return false;
      }

      current = current.parentElement;
    }

    return element.getClientRects().length > 0;
  };

  const hasCommerceOverlay = () => {
    if (document.querySelector('cart-drawer.active')) return true;
    if (document.body?.classList.contains('overflow-hidden')) return true;
    if (document.body?.classList.contains('overflow-hidden-tablet') && matchMedia('(max-width: 989px)').matches) return true;
    if (document.body?.classList.contains('overflow-hidden-mobile') && matchMedia('(max-width: 749px)').matches) return true;

    return VISIBLE_MODAL_SELECTORS.some((selector) =>
      Array.from(document.querySelectorAll(selector)).some(isVisible)
    );
  };

  let scheduled = false;

  const syncLauncherVisibility = () => {
    scheduled = false;
    document.documentElement.classList.toggle(ROOT_CLASS, hasCommerceOverlay());
    document.documentElement.classList.toggle(
      PURCHASE_CLASS,
      mobile.matches && Array.from(purchaseVisibility.values()).some(Boolean)
    );
  };

  const scheduleSync = () => {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(syncLauncherVisibility);
  };

  const handleOverlayTransition = (event) => {
    if (event.target instanceof Element && event.target.closest(OVERLAY_ROOT_SELECTOR)) {
      scheduleSync();
    }
  };

  const start = () => {
    if ('IntersectionObserver' in window) {
      purchaseObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => purchaseVisibility.set(entry.target, entry.isIntersecting));
        scheduleSync();
      });
      observePurchaseControls();
    }
    mobile.addEventListener?.('change', scheduleSync);
    syncLauncherVisibility();

    const touchesOverlay = (node) =>
      node instanceof Element &&
      (node.matches(OVERLAY_ROOT_SELECTOR) || Boolean(node.querySelector(OVERLAY_ROOT_SELECTOR)));
    const observer = new MutationObserver((records) => {
      const relevant = records.some((record) => {
        if (record.type === 'attributes') {
          return record.target === document.body ||
            (record.target instanceof Element && Boolean(record.target.closest(OVERLAY_ROOT_SELECTOR)));
        }
        return [...record.addedNodes, ...record.removedNodes].some(touchesOverlay);
      });
      const purchaseChanged = records.some((record) =>
        record.type === 'childList' && [...record.addedNodes, ...record.removedNodes].some((node) =>
          node instanceof Element &&
          (node.matches('variant-selects, product-form') || Boolean(node.querySelector('variant-selects, product-form')))
        )
      );
      if (purchaseChanged) observePurchaseControls();
      if (relevant || purchaseChanged) scheduleSync();
    });
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class', 'hidden', 'open', 'aria-hidden', 'aria-modal'],
    });

    document.addEventListener('toggle', handleOverlayTransition, true);
    document.addEventListener('transitionend', handleOverlayTransition, true);
    document.addEventListener('animationend', handleOverlayTransition, true);
    document.addEventListener('shopify:section:load', () => {
      observePurchaseControls();
      scheduleSync();
    });
    window.addEventListener('pageshow', scheduleSync);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
